import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ShieldCheck, Award, BookOpen } from 'lucide-react';
import axios from 'axios';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

export const AuthorPage = () => {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [cv, setCv] = useState({ featured_in: [], speaking: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAuthor = async () => {
      setLoading(true);
      try {
        const [res, cvRes] = await Promise.all([
          axios.get(`${API}/authors/${slug}`),
          axios.get(`${API}/authors/${slug}/cv`).catch(() => ({ data: { featured_in: [], speaking: [] } })),
        ]);
        if (!cancelled) {
          setAuthor(res.data);
          setCv(cvRes.data);
        }
      } catch (err) {
        if (!cancelled) setError('Author not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAuthor();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !author) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Author Not Found</h1>
        <Link to="/about" className="btn-editorial">Meet The Team</Link>
      </div>
    );
  }

  return (
    <div data-testid={`author-page-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={`${author.name} — ${author.title}`}
        description={author.bio}
        image={author.photo_url || author.image}
        url={`/authors/${author.slug}`}
        type="profile"
      />
      <SchemaOrg
        type="person"
        data={{
          name: author.name,
          jobTitle: author.title,
          bio: author.bio,
          image: author.photo_url || author.image,
          url: `/authors/${author.slug}`,
          sameAs: author.sameAs,
          knowsAbout: author.expertise,
          alumniOf: author.credentials,
        }}
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Authors', url: '/about' },
            { name: author.name, url: `/authors/${author.slug}` },
          ],
        }}
      />

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-20 lg:py-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/about" className="hover:text-[#111] transition-colors">Authors</Link>
            <span>/</span>
            <span className="text-[#111]">{author.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4">
              <div className="aspect-[3/4] overflow-hidden img-zoom">
                <img
                  src={author.photo_url || author.image}
                  alt={author.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-8">
              <p className="overline overline-gold mb-4">
                {author.credentials || 'Editorial Team'}
              </p>
              <h1 className="heading-editorial heading-editorial-lg text-[#111] mb-6">
                {author.name}
              </h1>
              <p className="font-heading italic text-2xl lg:text-3xl text-[#555] leading-snug mb-8">
                {author.title}
              </p>
              <p className="text-[#555] text-lg leading-relaxed mb-8 max-w-2xl">
                {author.bio}
              </p>

              {/* Credentials + Experience */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#E5E0D8] max-w-2xl">
                <div className="bg-[#FAF9F6] p-5">
                  <Award className="w-4 h-4 text-[#C69C6D] mb-2" strokeWidth={1.5} />
                  <p className="overline mb-1">Credentials</p>
                  <p className="text-sm font-heading text-[#111] leading-tight">
                    {author.credentials || '—'}
                  </p>
                </div>
                <div className="bg-[#FAF9F6] p-5">
                  <ShieldCheck className="w-4 h-4 text-[#C69C6D] mb-2" strokeWidth={1.5} />
                  <p className="overline mb-1">Experience</p>
                  <p className="text-sm font-heading text-[#111] leading-tight">
                    {author.experience || author.years_experience || '—'}
                  </p>
                </div>
                <div className="bg-[#FAF9F6] p-5">
                  <BookOpen className="w-4 h-4 text-[#C69C6D] mb-2" strokeWidth={1.5} />
                  <p className="overline mb-1">Articles</p>
                  <p className="text-sm font-heading text-[#111] leading-tight">
                    {author.article_count || 0} published
                  </p>
                </div>
              </div>

              {/* External profiles */}
              {Array.isArray(author.sameAs) && author.sameAs.length > 0 && (
                <div className="mt-8">
                  <p className="overline mb-3">Verified Profiles</p>
                  <ul className="flex flex-wrap gap-3">
                    {author.sameAs.map((url) => {
                      const host = (() => {
                        try {
                          return new URL(url).hostname.replace('www.', '');
                        } catch {
                          return url;
                        }
                      })();
                      return (
                        <li key={url}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer me"
                            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E5E0D8] hover:border-[#C69C6D] text-xs tracking-wider uppercase text-[#555] hover:text-[#111] transition-colors"
                            data-testid={`author-sameas-${host}`}
                          >
                            {host}
                            <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Specialties Strip */}
      {author.specialty && (
        <section className="bg-[#111] text-white">
          <div className="container-editorial py-12">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
              <p className="overline text-[#C69C6D]">Areas of Expertise</p>
              <p className="font-heading italic text-2xl lg:text-3xl text-white max-w-3xl text-right">
                {author.specialty}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Featured In + Speaking — CV-style trust signals */}
      {(cv.featured_in.length > 0 || cv.speaking.length > 0) && (
        <section className="section-editorial bg-white border-t border-[#E5E0D8]" data-testid="author-cv-section">
          <div className="container-editorial">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              {cv.featured_in.length > 0 && (
                <div>
                  <p className="overline overline-gold mb-6">Featured In</p>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#111] font-light mb-8 leading-tight">
                    Press &amp; <span className="italic">Citations</span>
                  </h2>
                  <ul className="space-y-4">
                    {cv.featured_in.map((entry, i) => (
                      <li key={i} className="text-[#555] leading-relaxed pb-4 border-b border-[#E5E0D8]">
                        {entry}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {cv.speaking.length > 0 && (
                <div>
                  <p className="overline overline-gold mb-6">Speaking &amp; Teaching</p>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#111] font-light mb-8 leading-tight">
                    Industry <span className="italic">Engagements</span>
                  </h2>
                  <ul className="space-y-4">
                    {cv.speaking.map((entry, i) => (
                      <li key={i} className="text-[#555] leading-relaxed pb-4 border-b border-[#E5E0D8]">
                        {entry}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Articles by author */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">Bylines</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">
                Articles by <span className="italic">{author.name.split(' ')[0]}</span>
              </h2>
            </div>
            <p className="hidden md:block overline">
              {author.article_count || author.articles?.length || 0} stories
            </p>
          </div>

          {!author.articles || author.articles.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[#E5E0D8]">
              <BookOpen className="w-8 h-8 text-[#C69C6D] mx-auto mb-4" strokeWidth={1.2} />
              <p className="overline overline-gold mb-2">In Production</p>
              <p className="text-[#555] max-w-sm mx-auto">
                {author.name.split(' ')[0]}'s next article is being drafted by the editorial team.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {author.articles.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group block"
                  data-testid={`author-article-${post.slug}`}
                >
                  {post.image_url && (
                    <div className="aspect-[4/5] overflow-hidden img-zoom mb-6">
                      <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <p className="overline mb-3 flex items-center gap-3">
                    <span>{post.category || 'Editorial'}</span>
                    <span className="w-6 h-px bg-[#C69C6D]" />
                    <span className="text-[#888]">{formatDate(post.published_at)}</span>
                  </p>
                  <h3 className="font-heading text-2xl font-light leading-tight tracking-tight text-[#111] mb-3 group-hover:text-[#C69C6D] transition-colors duration-500">
                    {post.title}
                  </h3>
                  <p className="text-[#555] leading-relaxed line-clamp-2">{post.excerpt}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Back to team */}
      <section className="bg-[#111] text-white py-16">
        <div className="container-editorial">
          <Link to="/about" className="group inline-flex items-center gap-3 text-xs tracking-[0.15em] uppercase">
            <ArrowLeft className="w-4 h-4 text-[#C69C6D] group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
            Meet The Full Team
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AuthorPage;
